import type {
  Member,
  PatchAdminV1OrgMembersIdBody,
  PatchAdminV1OrgMembersIdBodyGrantedPermissionsItem,
  PostAdminV1OrgMembersInvitationsBody,
} from '@nucleus-commerce/api-client/admin';

import type {
  EditMemberInput,
  EditMemberOutput,
  InviteMemberOutput,
} from '../schemas/member-form-schema';
import { isOwner } from '../schemas/member-rules';

type PermissionKey = PatchAdminV1OrgMembersIdBodyGrantedPermissionsItem;

/** An empty `storeIds` means every store, now and in the future. */
const storeIdsFor = (values: Pick<EditMemberOutput, 'access' | 'storeIds'>): string[] =>
  values.access === 'all' ? [] : values.storeIds;

/** Form values hold plain strings; the checklists only offer catalog keys. */
const permissionKeys = (values: readonly string[]): PermissionKey[] =>
  [...values] as PermissionKey[];

export function toInvitation(values: InviteMemberOutput): PostAdminV1OrgMembersInvitationsBody {
  return {
    email: values.email.trim(),
    name: values.name,
    roleKey: values.roleKey,
    storeIds: storeIdsFor(values),
  };
}

/**
 * The whole access of a member. The owner holds everything everywhere, so it is sent without
 * extras, removals or store roles; in the same-role mode every store role goes; a store row
 * left on "default", or one the member can no longer reach, is not sent.
 */
export function toMemberUpdate(values: EditMemberOutput): PatchAdminV1OrgMembersIdBody {
  const storeIds = storeIdsFor(values);
  const owner = isOwner(values.roleKey);
  const reachable = (storeId: string) => storeIds.length === 0 || storeIds.includes(storeId);
  const storeRoles =
    owner || values.scope === 'same'
      ? []
      : values.storeRoles
          .filter((row) => row.roleKey !== '' && reachable(row.storeId))
          .map((row) => ({
            storeId: row.storeId,
            roleKey: row.roleKey,
            grantedPermissions: permissionKeys(row.grantedPermissions),
            revokedPermissions: permissionKeys(row.revokedPermissions),
          }));
  return {
    roleKey: values.roleKey,
    storeIds,
    grantedPermissions: owner ? [] : permissionKeys(values.grantedPermissions),
    revokedPermissions: owner ? [] : permissionKeys(values.revokedPermissions),
    storeRoles,
  };
}

export function fromMember(member: Member): EditMemberInput {
  return {
    roleKey: member.role.key,
    access: member.storeIds.length === 0 ? 'all' : 'selected',
    storeIds: member.storeIds,
    grantedPermissions: member.grantedPermissions,
    revokedPermissions: member.revokedPermissions,
    scope: member.storeRoles.length > 0 ? 'perStore' : 'same',
    storeRoles: member.storeRoles.map((entry) => ({
      storeId: entry.storeId,
      roleKey: entry.role.key,
      grantedPermissions: entry.grantedPermissions,
      revokedPermissions: entry.revokedPermissions,
    })),
  };
}

/** Anything beyond one plain role: extra or removed permissions, or a different role in a store. */
export function hasCustomAccess(member: Member): boolean {
  return (
    member.grantedPermissions.length > 0 ||
    member.revokedPermissions.length > 0 ||
    member.storeRoles.length > 0
  );
}
